import {
  QButton,
  QCloseButton,
  QLookup,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSelect,
  QSelectItem,
  QText,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { removeUsersfromGroup } from '../../api/medtech-api';
import { getContentHiddenWarningKey, getErrorCode } from '../../errors/um-errors';
import { useGroupLookupView } from '../../hooks/useGroupLookupView';
import { Group, ModalComponentProps, RemoveGroupFromUserPayload, User } from '../../types';
import { useFlagProvider } from '../../utils/flagProvider';
import { removeUsersFromGroupsUnlessContentHidden } from '../../utils/groups.utils';
import { createErrorToast, createSuccessToast, createWarningToast } from '../../utils/toast.utils';
import { ContentHiddenConfirmationModal } from './ContentHiddenConfirmationModal';

type RemoveUserGroupsModalProps = ModalComponentProps & {
  users: User[];
  getUsers: () => Promise<void>;
};

const RemoveUserGroupsModalLegacy = ({ closeModal, users, getUsers }: RemoveUserGroupsModalProps) => {
  const { t } = useTranslation();
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const [group, setGroup] = useState<QSelectItem>();
  const [isRemoving, setRemoving] = useState<boolean>(false);
  const { showToast } = useToastProvider();

  const handleSubmit = (isForce: boolean) => {
    setRemoving(true);
    const payload: RemoveGroupFromUserPayload = {
      groups: [+group!.value],
      users: users.map((user) => user.id),
      forceRemove: isForce,
    };

    removeUsersfromGroup(payload)
      .then(async () => {
        await getUsers();
        setRemoving(false);
        showToast(
          createSuccessToast(
            t('messages.success.usersRemoved', { count: users.length }),
            'group-remove-users-success-toast',
          ),
        );
        closeModal();
      })
      .catch((err) => {
        setRemoving(false);
        const warningKey = getContentHiddenWarningKey(getErrorCode(err));
        if (warningKey) {
          setConfirmationModalOpen(true);
          showToast(createWarningToast(t(warningKey), 'group-remove-users-warning-toast'));
        } else {
          showToast(createErrorToast(t('messages.error.removeUsersFromGroup'), 'group-remove-users-error-toast'));
        }
      });
  };

  const options = useMemo(() => {
    const uniqueGroups = new Map<number, QSelectItem>();
    users
      .flatMap((user) => user.groups)
      .forEach(({ id, name }) => {
        if (!uniqueGroups.has(id)) {
          uniqueGroups.set(id, { label: name.toUpperCase(), value: id.toString() });
        }
      });
    return Array.from(uniqueGroups.values());
  }, [users]);

  return (
    <QModal onClose={closeModal} size="md" isOpen>
      <QModalHeader>
        <QText data-cy="heading">{t('modals.removeFromGroup.title')}</QText>
        <QCloseButton onClick={closeModal} />
      </QModalHeader>
      <QModalBody>
        <QSelect
          aria-label="group"
          value={group?.label}
          onChange={(value) => value && setGroup(value)}
          options={options}
        />
        {isConfirmationModalOpen && (
          <ContentHiddenConfirmationModal
            closeModal={() => setConfirmationModalOpen(false)}
            onConfirm={() => handleSubmit(true)}
            isLoading={isRemoving}
          />
        )}
      </QModalBody>
      <QModalActions>
        <QButton data-cy="cancel-button" variant="outline" onClick={closeModal}>
          {t('common.cancel')}
        </QButton>
        <QButton
          data-cy="remove-button"
          isDestructive
          isDisabled={!group}
          isLoading={isRemoving}
          onClick={() => handleSubmit(false)}
        >
          {t('common.remove')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

const RemoveUserGroupsModalQLookup = ({ closeModal, users, getUsers }: RemoveUserGroupsModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const [isRemoving, setRemoving] = useState(false);
  const [isForceConfirmOpen, setForceConfirmOpen] = useState(false);
  const [groupsNeedingForce, setGroupsNeedingForce] = useState<number[]>([]);

  const availableGroups = useMemo(() => {
    const uniqueGroups = new Map<number, Group>();
    users
      .flatMap((u) => u.groups)
      .forEach((g) => {
        if (!uniqueGroups.has(g.id)) uniqueGroups.set(g.id, g);
      });
    return Array.from(uniqueGroups.values());
  }, [users]);

  const { groupDataView, filterOption } = useGroupLookupView();

  const onSelect = useCallback(
    async (selectedGroups: readonly Group[]) => {
      if (isRemoving) return;
      setRemoving(true);
      const userIds = users.map((u) => u.id);
      const selectedGroupIds = selectedGroups.map((g) => g.id);

      try {
        const { groupIdsNeedingForce, warningKeys } = await removeUsersFromGroupsUnlessContentHidden(
          selectedGroupIds,
          userIds,
        );

        if (groupIdsNeedingForce.length > 0) {
          warningKeys.forEach((warningKey) =>
            showToast(createWarningToast(t(warningKey), 'group-remove-users-warning-toast')),
          );
          setGroupsNeedingForce(groupIdsNeedingForce);
          setForceConfirmOpen(true);
        } else {
          await getUsers();
          showToast(
            createSuccessToast(
              t('messages.success.usersRemoved', { count: users.length }),
              'group-remove-users-success-toast',
            ),
          );
          closeModal();
        }
      } catch {
        showToast(createErrorToast(t('messages.error.removeUsersFromGroup'), 'group-remove-users-error-toast'));
        closeModal();
      } finally {
        setRemoving(false);
      }
    },
    [isRemoving, users, getUsers, closeModal, showToast, t],
  );

  const handleForceConfirm = useCallback(async () => {
    setForceConfirmOpen(false);
    setRemoving(true);
    const userIds = users.map((u) => u.id);
    try {
      await Promise.all(
        groupsNeedingForce.map((groupId) =>
          removeUsersfromGroup({ groups: [groupId], users: userIds, forceRemove: true }),
        ),
      );
      await getUsers();
      showToast(
        createSuccessToast(
          t('messages.success.usersRemoved', { count: users.length }),
          'group-remove-users-success-toast',
        ),
      );
      closeModal();
    } catch {
      showToast(createErrorToast(t('messages.error.removeUsersFromGroup'), 'group-remove-users-error-toast'));
      closeModal();
    } finally {
      setRemoving(false);
      setGroupsNeedingForce([]);
    }
  }, [groupsNeedingForce, users, getUsers, closeModal, showToast, t]);

  return (
    <>
      <QLookup.DataProvider.Fixed data={availableGroups} filterOption={filterOption}>
        <QLookup.MultiSelect<Group>
          isOpen
          action={isRemoving ? t('modals.removeFromGroup.removing') : t('common.remove')}
          searchPlaceholder={t('modals.manageGroups.searchPlaceholder')}
          accessors={{ id: 'id' }}
          isItemDisabled={() => false}
          isItemPreSelected={() => false}
          onSelect={onSelect}
          onCancel={closeModal}
          view={groupDataView}
          defaultSortBy={{ id: 'name' }}
        >
          <QModalHeader>
            <QText>{t('modals.removeFromGroup.title')}</QText>
          </QModalHeader>
        </QLookup.MultiSelect>
      </QLookup.DataProvider.Fixed>

      {isForceConfirmOpen && (
        <ContentHiddenConfirmationModal
          closeModal={() => setForceConfirmOpen(false)}
          onConfirm={handleForceConfirm}
          isLoading={isRemoving}
        />
      )}
    </>
  );
};

export const RemoveUserGroupsModal = (props: RemoveUserGroupsModalProps) => {
  const { isFlagEnabled } = useFlagProvider();
  return isFlagEnabled('multiGroupAssignment') ? (
    <RemoveUserGroupsModalQLookup {...props} />
  ) : (
    <RemoveUserGroupsModalLegacy {...props} />
  );
};
